import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { SectionSidebarNav, type SectionSidebarNavButtonItem } from "@/components/SectionSidebarNav";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { useCurrentUser, userIsAbrechnung, userIsBuero } from "@/lib/auth";
import { isElectronWithOpenFolder } from "@/lib/electron";
import {
  getTimesheetColumnSettings,
  getTimesheetSummaryFacets,
  listTimesheetsSummary,
  listUnreadBillingAnswers,
  saveTimesheetColumnSettings,
  timesheetAtContactFields,
  type TimesheetColumnSettings,
  type TimesheetColumnView,
  type TimesheetSummary,
  type TimesheetSummaryFacets,
  type TimesheetSummaryYear,
  type UnreadBillingAnswer,
} from "@/lib/timesheets";
import { getProjectTableHeroFields } from "@/lib/projects";
import {
  fetchProjectCompletions,
  filterProjectCompletions,
  projectCompletionFiltersActive,
  sortProjectCompletions,
  type ProjectCompletionClientFilters,
  type ProjectCompletionRow,
} from "@/lib/projectCompletions";
import {
  clampTimesheetColumnWidth,
  resolveTimesheetColumns,
  timesheetRowAsOrder,
} from "@/pages/stundennachweise/columns";
import { useTimesheetFlow } from "@/features/timesheetFlow/useTimesheetFlow";
import { wireTimesheetModalRefresh } from "@/lib/timesheetModalBridge";
import { getTimesheetStatus } from "@/pages/stundennachweise/timesheetFilterLogic";
import { useRealtime } from "@/lib/useRealtime";
import { CollapsibleSection } from "@/components/ui/CollapsibleSection";
import { FilterBar } from "@/pages/stundennachweise/FilterBar";
import { BillingUnreadAnswersBanner } from "@/pages/stundennachweise/BillingUnreadAnswersBanner";
import { ProjectCompletionsFilterBar } from "@/pages/stundennachweise/ProjectCompletionsFilterBar";
import {
  ProjectCompletionsTable,
  projectCompletionsShowElectronFolder,
} from "@/pages/stundennachweise/ProjectCompletionsTable";
import { TimesheetSummaryPanels } from "@/pages/stundennachweise/TimesheetSummaryPanels";
import { TimesheetTable } from "@/pages/stundennachweise/TimesheetTable";
import {
  deriveOrdnerForYear,
  ensureActiveYearFromFacets,
  filterTimesheets,
} from "@/pages/stundennachweise/timesheetFilterLogic";
import {
  buildTimesheetFilterHeading,
  buildTimesheetFilterSubtitle,
} from "@/pages/stundennachweise/timesheetFilterSummary";
import {
  timesheetHasActiveFilters,
  useTimesheetFilters,
} from "@/pages/stundennachweise/useTimesheetFilters";
import { SearchInput } from "@/pages/projekte/SearchInput";
import type { HeroCustomField } from "@/pages/projekte/columns";
import { TimesheetColumnsDialog } from "@/pages/stundennachweise/TimesheetColumnsDialog";
import { useHoursUsage } from "@/pages/projekte/useHoursUsage";

type TimesheetPageTab = "timesheets" | "project-completions";

const DEFAULT_COMPLETION_FILTERS = {
  kunde: "",
  status: "all" as const,
};

export function StundennachweiseIndexPage({
  section,
}: {
  /** Festgelegter Bereich (z. B. unter Dokumente-Sektion für Büro). */
  section?: TimesheetPageTab;
} = {}) {
  const { user } = useCurrentUser();
  const location = useLocation();
  const isBuero = userIsBuero(user);
  const isAbrechnung = userIsAbrechnung(user);
  const { openCreate, openEdit, openDetail, refreshKey, registerListAtContactPatch } =
    useTimesheetFlow();
  const { filters, update, reset } = useTimesheetFilters();

  const inDokumenteSection = location.pathname.startsWith("/dokumente/");
  const [activeTab, setActiveTab] = useState<TimesheetPageTab>(section ?? "timesheets");

  useEffect(() => {
    if (section) setActiveTab(section);
  }, [section]);
  const [allTimesheets, setAllTimesheets] = useState<TimesheetSummary[]>([]);
  const [facets, setFacets] = useState<TimesheetSummaryFacets | null>(null);
  const [unreadBillingItems, setUnreadBillingItems] = useState<UnreadBillingAnswer[]>([]);
  const [allCompletions, setAllCompletions] = useState<ProjectCompletionRow[]>([]);
  const [completionFilters, setCompletionFilters] =
    useState<ProjectCompletionClientFilters>(DEFAULT_COMPLETION_FILTERS);
  const [loading, setLoading] = useState(true);
  const [completionsLoading, setCompletionsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completionsError, setCompletionsError] = useState<string | null>(null);
  const [columnSettings, setColumnSettings] = useState<TimesheetColumnSettings>({
    desktop: null,
    mobile: null,
  });
  const [heroFields, setHeroFields] = useState<HeroCustomField[]>([]);
  const [columnsDialogOpen, setColumnsDialogOpen] = useState(false);
  const isMountedRef = useRef(true);
  const widthSaveTimerRef = useRef<number | null>(null);
  const pendingSettingsRef = useRef<TimesheetColumnSettings | null>(null);
  const timesheetsLoadRunRef = useRef(0);
  const timesheetsReloadTimerRef = useRef<number | null>(null);
  const hasTimesheetsDataRef = useRef(false);
  const hasCompletionsDataRef = useRef(false);
  const completionsReloadTimerRef = useRef<number | null>(null);
  const activeJahrRef = useRef<TimesheetSummaryYear>(filters.jahr);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (widthSaveTimerRef.current) {
        window.clearTimeout(widthSaveTimerRef.current);
      }
      if (timesheetsReloadTimerRef.current) {
        window.clearTimeout(timesheetsReloadTimerRef.current);
      }
      if (completionsReloadTimerRef.current) {
        window.clearTimeout(completionsReloadTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getTimesheetColumnSettings().catch(() => ({ desktop: null, mobile: null })),
      getProjectTableHeroFields().catch(() => []),
    ]).then(([settings, fields]) => {
      if (cancelled) return;
      setColumnSettings(settings);
      setHeroFields(fields);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const showOpenFolderColumn = useMemo(
    () => userIsBuero(user) && isElectronWithOpenFolder(),
    [user],
  );

  const columnOptions = useMemo(
    () => ({
      user,
      heroFields,
      showOpenFolderColumn,
    }),
    [user, heroFields, showOpenFolderColumn],
  );

  const isMobile = useMediaQuery("(max-width: 767px)");

  const visibleColumns = useMemo(
    () =>
      resolveTimesheetColumns(
        isMobile ? columnSettings.mobile?.columns : columnSettings.desktop?.columns,
        columnOptions,
      ),
    [isMobile, columnSettings, columnOptions],
  );

  const visibleWidths = useMemo(() => {
    const view = isMobile ? columnSettings.mobile : columnSettings.desktop;
    const widths = view?.widths ?? {};
    const out: Record<string, number> = {};
    for (const [key, value] of Object.entries(widths)) {
      const num = Number(value);
      if (Number.isFinite(num) && num > 0) out[key] = clampTimesheetColumnWidth(num);
    }
    return out;
  }, [isMobile, columnSettings]);

  // Hero-Custom-Fields werden nur für Custom-Spalten (cf_*) bzw. den
  // Stundenverbrauch gebraucht. Beim Standard-Aufruf bleiben sie weg, damit die
  // Summary-Payload klein bleibt; sobald eine solche Spalte sichtbar ist, wird
  // die Liste einmal mit Hero-Feldern nachgeladen.
  const needsHeroCustomFields = useMemo(
    () =>
      visibleColumns.includes("hours_usage") ||
      visibleColumns.some((col) => col.startsWith("cf_")),
    [visibleColumns],
  );
  const needsHeroCustomFieldsRef = useRef(needsHeroCustomFields);
  needsHeroCustomFieldsRef.current = needsHeroCustomFields;

  async function handleSaveColumnSettings(next: TimesheetColumnSettings) {
    try {
      const saved = await saveTimesheetColumnSettings(next);
      if (isMountedRef.current) setColumnSettings(saved);
    } catch (err) {
      console.warn("[Stundennachweise] saveColumnSettings failed:", err);
      if (isMountedRef.current) setColumnSettings(next);
    }
  }

  const handleColumnWidthChange = useCallback(
    (key: string, width: number) => {
      const clamped = clampTimesheetColumnWidth(width);
      setColumnSettings((prev) => {
        const viewKey: "desktop" | "mobile" = isMobile ? "mobile" : "desktop";
        const current: TimesheetColumnView = prev[viewKey] ?? { columns: [], widths: {} };
        const next: TimesheetColumnSettings = {
          ...prev,
          [viewKey]: {
            columns: current.columns ?? [],
            widths: { ...(current.widths ?? {}), [key]: clamped },
          },
        };
        pendingSettingsRef.current = next;
        return next;
      });

      if (widthSaveTimerRef.current) {
        window.clearTimeout(widthSaveTimerRef.current);
      }
      widthSaveTimerRef.current = window.setTimeout(() => {
        const payload = pendingSettingsRef.current;
        pendingSettingsRef.current = null;
        widthSaveTimerRef.current = null;
        if (!payload) return;
        void saveTimesheetColumnSettings(payload).catch((err) => {
          console.warn("[Stundennachweise] saveColumnWidths failed:", err);
        });
      }, 500);
    },
    [isMobile],
  );

  const patchTimesheetAtContact = useCallback((timesheetId: number, atContact: boolean) => {
    setAllTimesheets((prev) =>
      prev.map((ts) =>
        ts.id === timesheetId ? { ...ts, ...timesheetAtContactFields(atContact) } : ts,
      ),
    );
  }, []);

  useEffect(() => registerListAtContactPatch(patchTimesheetAtContact), [
    registerListAtContactPatch,
    patchTimesheetAtContact,
  ]);

  const loadTimesheets = useCallback(async (options?: { silent?: boolean; jahr?: TimesheetSummaryYear }) => {
    const runId = ++timesheetsLoadRunRef.current;
    const silent = options?.silent ?? hasTimesheetsDataRef.current;
    if (!silent) {
      setLoading(true);
    }
    setError(null);
    try {
      const list = await listTimesheetsSummary({
        includeHeroCustomFields: needsHeroCustomFieldsRef.current,
        jahr: options?.jahr ?? activeJahrRef.current,
      });
      if (!isMountedRef.current || runId !== timesheetsLoadRunRef.current) return;
      hasTimesheetsDataRef.current = list.length > 0;
      setAllTimesheets(list);
    } catch (err) {
      if (!isMountedRef.current || runId !== timesheetsLoadRunRef.current) return;
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Stundennachweise konnten nicht geladen werden.";
      setError(message);
      hasTimesheetsDataRef.current = false;
      setAllTimesheets([]);
    } finally {
      if (isMountedRef.current && runId === timesheetsLoadRunRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const loadFacets = useCallback(async () => {
    try {
      const next = await getTimesheetSummaryFacets();
      if (isMountedRef.current) setFacets(next);
    } catch {
      // Facetten sind optional – ohne sie fallen die Jahres-Chips auf das aktive Jahr zurück.
    }
  }, []);

  const loadUnreadBillingAnswers = useCallback(async () => {
    if (!isAbrechnung) {
      setUnreadBillingItems([]);
      return;
    }
    try {
      const items = await listUnreadBillingAnswers();
      if (isMountedRef.current) setUnreadBillingItems(items);
    } catch {
      if (isMountedRef.current) setUnreadBillingItems([]);
    }
  }, [isAbrechnung]);

  const scheduleTimesheetsReload = useCallback(() => {
    if (timesheetsReloadTimerRef.current != null) {
      window.clearTimeout(timesheetsReloadTimerRef.current);
    }
    timesheetsReloadTimerRef.current = window.setTimeout(() => {
      timesheetsReloadTimerRef.current = null;
      void loadTimesheets({ silent: true });
      void loadFacets();
      void loadUnreadBillingAnswers();
    }, 500);
  }, [loadTimesheets, loadFacets, loadUnreadBillingAnswers]);

  const loadCompletions = useCallback(async (search?: string, options?: { silent?: boolean }) => {
    const silent = options?.silent ?? hasCompletionsDataRef.current;
    if (!silent) {
      setCompletionsLoading(true);
    }
    setCompletionsError(null);
    try {
      const list = await fetchProjectCompletions(search);
      if (!isMountedRef.current) return;
      hasCompletionsDataRef.current = list.length > 0;
      setAllCompletions(list);
    } catch (err) {
      if (!isMountedRef.current) return;
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Fertigstellungsmeldungen konnten nicht geladen werden.";
      setCompletionsError(message);
      hasCompletionsDataRef.current = false;
      setAllCompletions([]);
    } finally {
      if (isMountedRef.current) setCompletionsLoading(false);
    }
  }, []);

  const scheduleCompletionsReload = useCallback(
    (search?: string) => {
      if (completionsReloadTimerRef.current != null) {
        window.clearTimeout(completionsReloadTimerRef.current);
      }
      completionsReloadTimerRef.current = window.setTimeout(() => {
        completionsReloadTimerRef.current = null;
        void loadCompletions(search, { silent: true });
      }, 500);
    },
    [loadCompletions],
  );

  // Aktives Jahr (nach Facetten-Korrektur) bestimmt, welches Jahr serverseitig
  // geladen wird. Hängt nur an Facetten + URL-Filter – nicht an der geladenen
  // Liste, damit keine Load→Daten→Jahr→Load-Schleife entsteht.
  const effectiveJahr = useMemo(
    () => ensureActiveYearFromFacets(facets, filters.jahr),
    [facets, filters.jahr],
  );

  useEffect(() => {
    activeJahrRef.current = effectiveJahr;
  }, [effectiveJahr]);

  useEffect(() => {
    if (effectiveJahr !== filters.jahr) {
      update({ jahr: effectiveJahr });
    }
  }, [effectiveJahr, filters.jahr, update]);

  useEffect(() => {
    void loadFacets();
  }, [loadFacets, refreshKey]);

  useEffect(() => {
    void loadUnreadBillingAnswers();
  }, [loadUnreadBillingAnswers, refreshKey]);

  useEffect(() => {
    // Lädt nur das aktive Jahr (serverseitige Begrenzung). Reagiert zusätzlich auf
    // `needsHeroCustomFields`: wird eine Custom-/Stundenverbrauch-Spalte aktiviert,
    // lädt die Liste einmal mit Hero-Feldern nach.
    void loadTimesheets({ jahr: effectiveJahr });
  }, [loadTimesheets, refreshKey, needsHeroCustomFields, effectiveJahr]);

  useEffect(() => {
    if (activeTab !== "project-completions" || !isBuero) return;
    void loadCompletions(filters.q);
  }, [activeTab, isBuero, loadCompletions, filters.q, refreshKey]);

  useEffect(() => wireTimesheetModalRefresh(scheduleTimesheetsReload), [scheduleTimesheetsReload]);

  useRealtime({
    scopes: ["timesheets:list"],
    domains: ["timesheets"],
    events: [
      "timesheets_changed",
      "timesheet_created",
      "timesheet_updated",
      "timesheet_customer_signed",
      "timesheet_company_signed",
      "timesheet_deleted",
      "timesheet_pdf_generated",
      "timesheet_status_changed",
      "timesheet_billing_question_answered",
      "timesheet_billing_seen",
    ],
    onChange: scheduleTimesheetsReload,
    enabled: !!user && activeTab === "timesheets",
  });

  useRealtime({
    scopes: ["badge:project-completions"],
    domains: ["auftraege", "projects", "timesheets"],
    events: [
      "auftrag_neu",
      "auftraege_changed",
      "projects_changed",
      "timesheets_changed",
    ],
    onChange: () => {
      scheduleCompletionsReload(filters.q);
    },
    enabled: !!user && isBuero && activeTab === "project-completions",
  });

  const effectiveFilters = useMemo(
    () => (effectiveJahr === filters.jahr ? filters : { ...filters, jahr: effectiveJahr }),
    [effectiveJahr, filters],
  );

  // Jahres-Chips aus den Facetten; das aktuell gewählte Jahr immer einschließen,
  // damit der aktive Chip auch dann sichtbar ist, wenn die Facetten noch laden.
  const yearChips = useMemo(() => {
    const set = new Set<number>(facets?.years ?? []);
    if (typeof effectiveJahr === "number") set.add(effectiveJahr);
    if (!set.size) set.add(new Date().getFullYear());
    return [...set].sort((a, b) => b - a);
  }, [facets, effectiveJahr]);

  const filteredRows = useMemo(
    () => filterTimesheets(allTimesheets, effectiveFilters, user),
    [allTimesheets, effectiveFilters, user],
  );

  const hoursUsageOrders = useMemo(
    () => filteredRows.map(timesheetRowAsOrder).filter((o) => o.id > 0),
    [filteredRows],
  );
  const showHoursUsage = visibleColumns.includes("hours_usage");
  const hoursUsageById = useHoursUsage(hoursUsageOrders, showHoursUsage);

  const ordnerBeschreibung = useMemo(() => {
    if (typeof effectiveFilters.jahr !== "number" || !effectiveFilters.ordner) {
      return "";
    }
    const ordner = deriveOrdnerForYear(
      allTimesheets,
      effectiveFilters.jahr,
      effectiveFilters.kunde,
    );
    return ordner.find((o) => o.nummer === effectiveFilters.ordner)?.beschreibung || "";
  }, [allTimesheets, effectiveFilters]);

  const sordnerBeschreibung = useMemo(() => {
    if (effectiveFilters.jahr !== "S" || !effectiveFilters.sordner) return "";
    const match = allTimesheets.find(
      (ts) =>
        String(ts.ordnernummer || "").toUpperCase() === effectiveFilters.sordner.toUpperCase() ||
        String(ts.auftragsnummer || "")
          .toUpperCase()
          .startsWith(`${effectiveFilters.sordner.toUpperCase()}#`),
    );
    return match ? String(match.ordner_beschreibung || match.ordnerbeschreibung || "") : "";
  }, [allTimesheets, effectiveFilters]);

  const filteredCompletionRows = useMemo(() => {
    const filtered = filterProjectCompletions(allCompletions, {
      ...completionFilters,
      q: filters.q,
    });
    return sortProjectCompletions(filtered);
  }, [allCompletions, completionFilters, filters.q]);

  const showElectronFolder = projectCompletionsShowElectronFolder(isBuero);

  const isCompletionsTab = activeTab === "project-completions" && isBuero;

  const filterHeading = useMemo(() => {
    if (isCompletionsTab) {
      return filters.q.trim() ? `Suchergebnisse für „${filters.q.trim()}“` : "Fertigstellungsmeldungen";
    }
    return buildTimesheetFilterHeading(
      effectiveFilters,
      effectiveFilters.jahr === "S" ? sordnerBeschreibung : ordnerBeschreibung,
    );
  }, [
    isCompletionsTab,
    filters.q,
    effectiveFilters,
    ordnerBeschreibung,
    sordnerBeschreibung,
  ]);

  const filterSubtitle = useMemo(() => {
    if (isCompletionsTab) {
      return filters.q.trim()
        ? "Gefilterte Fertigstellungsmeldungen."
        : "Übersicht aller abgeschlossenen Projekte inklusive Lieferscheinen und Rechnungen.";
    }
    return buildTimesheetFilterSubtitle(effectiveFilters);
  }, [isCompletionsTab, filters.q, effectiveFilters]);

  function handleRowOpen(row: TimesheetSummary) {
    if (getTimesheetStatus(row) === "draft") {
      openEdit(row.id);
      return;
    }
    openDetail(row.id);
  }

  function handleContactAgingFilter(term: string) {
    update({
      q: term,
      contactAgingSearch: !!term.trim(),
    });
  }

  const countLabel = isCompletionsTab
    ? completionsLoading && filteredCompletionRows.length === 0
      ? "· Lade …"
      : filteredCompletionRows.length === 1
        ? "· 1 Meldung"
        : `· ${filteredCompletionRows.length} Meldungen`
    : loading && filteredRows.length === 0
      ? "· Lade …"
      : filteredRows.length === 1
        ? "· 1 Nachweis"
        : `· ${filteredRows.length} Nachweise`;

  function handleCompletionFiltersChange(patch: Partial<ProjectCompletionClientFilters>) {
    setCompletionFilters((prev) => ({ ...prev, ...patch }));
  }

  function resetCompletionFilters() {
    setCompletionFilters(DEFAULT_COMPLETION_FILTERS);
    update({ q: "" });
  }

  function handleSearchChange(q: string) {
    update({
      q,
      contactAgingSearch: false,
    });
  }

  const sectionNavItems = useMemo<SectionSidebarNavButtonItem[]>(
    () => [
      {
        key: "timesheets",
        label: "Stundennachweise",
        active: activeTab === "timesheets",
        onClick: () => setActiveTab("timesheets"),
      },
      {
        key: "project-completions",
        label: "Fertigstellungsmeldungen",
        tourTarget: "stundennachweis-tab-fertigstellung",
        active: activeTab === "project-completions",
        onClick: () => setActiveTab("project-completions"),
      },
    ],
    [activeTab],
  );

  const pageContent = (
    <div className="space-y-4 md:space-y-5">
        {!isCompletionsTab && isAbrechnung ? (
          <BillingUnreadAnswersBanner
            items={unreadBillingItems}
            onOpen={(id) => openDetail(id, { initialTab: "billing" })}
          />
        ) : null}

        <section className="rounded-ios-lg bg-card px-4 py-4 shadow-ios-md ring-1 ring-separator/35 dark:ring-separator/50 md:px-5 md:py-4">
          <div className="flex flex-col gap-3 md:gap-4 lg:flex-row lg:items-center lg:justify-between lg:gap-5">
            <div className="min-w-0 flex-1 space-y-1 lg:max-w-[min(100%,40rem)]">
              <h2 className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-lg font-semibold leading-snug tracking-tight text-label md:text-xl">
                <span className="min-w-0 break-words">{filterHeading}</span>
                <span
                  className="shrink-0 whitespace-nowrap text-sm font-medium tabular-nums text-label-secondary md:text-[15px]"
                  aria-live="polite"
                >
                  {countLabel}
                </span>
              </h2>
              <p className="text-xs text-label-secondary md:text-sm">{filterSubtitle}</p>
            </div>
            <div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:items-center lg:max-w-lg">
              <SearchInput
                value={filters.q}
                onChange={handleSearchChange}
                placeholder={
                  isCompletionsTab
                    ? "Fertigstellungsmeldungen durchsuchen…"
                    : "Stundennachweise durchsuchen…"
                }
                className="min-w-0 flex-1"
              />
              {!isCompletionsTab ? (
                <Button
                  type="button"
                  variant="secondary"
                  className="hidden shrink-0 whitespace-nowrap md:inline-flex"
                  onClick={() => setColumnsDialogOpen(true)}
                >
                  Spalten
                </Button>
              ) : null}
              <Button type="button" data-tour="stundennachweis-neu" onClick={openCreate} className="shrink-0">
                {isCompletionsTab ? "Fertigstellung anlegen" : "Neu"}
              </Button>
            </div>
          </div>
        </section>

        {isCompletionsTab ? (
          <>
            <CollapsibleSection
              sectionKey="stundennachweise.fertigstellungen.filter"
              title="Filter"
              summary={
                projectCompletionFiltersActive({ ...completionFilters, q: filters.q })
                  ? "Filter aktiv"
                  : "Alle Fertigstellungen"
              }
              actions={
                projectCompletionFiltersActive({ ...completionFilters, q: filters.q }) ? (
                  <button
                    type="button"
                    onClick={resetCompletionFilters}
                    className="rounded-lg px-2 py-1 text-xs font-medium text-accent transition hover:bg-fill-tertiary"
                  >
                    Zurücksetzen
                  </button>
                ) : null
              }
            >
              <ProjectCompletionsFilterBar
                filters={completionFilters}
                searchQuery={filters.q}
                rows={allCompletions}
                onChange={handleCompletionFiltersChange}
                onReset={resetCompletionFilters}
              />
            </CollapsibleSection>

            {completionsError ? (
              <Alert variant="error" title="Liste konnte nicht geladen werden">
                {completionsError}
              </Alert>
            ) : null}

            <section className="space-y-3">
              {completionsLoading && allCompletions.length === 0 ? (
                <div className="flex items-center justify-center py-16 text-label-secondary">
                  <Spinner />
                  <span className="ml-2 text-sm">Fertigstellungsmeldungen werden geladen …</span>
                </div>
              ) : (
                <>
                  <ProjectCompletionsTable
                    rows={filteredCompletionRows}
                    loading={completionsLoading}
                    showElectronFolder={showElectronFolder}
                  />
                  {!completionsLoading &&
                  filteredCompletionRows.length === 0 &&
                  !completionsError &&
                  projectCompletionFiltersActive({
                    ...completionFilters,
                    q: filters.q,
                  }) ? (
                    <div className="mt-4 flex flex-col items-center gap-3 rounded-ios bg-fill-quaternary/25 px-4 py-4 text-center shadow-ios ring-1 ring-separator/30 dark:ring-separator/45">
                      <p className="max-w-md text-sm text-label-secondary">
                        Für die aktuelle Auswahl wurden keine Fertigstellungsmeldungen gefunden.
                        Filter zurücksetzen, um die Chance auf Treffer zu erhöhen.
                      </p>
                      <Button type="button" variant="secondary" onClick={resetCompletionFilters}>
                        Filter zurücksetzen
                      </Button>
                    </div>
                  ) : null}
                </>
              )}
            </section>
          </>
        ) : (
          <>
            <CollapsibleSection
              sectionKey="stundennachweise.filter"
              title="Filter"
              summary={
                timesheetHasActiveFilters(effectiveFilters) ? "Filter aktiv" : "Alle Stundennachweise"
              }
              mobileAlwaysExpanded
              actions={
                timesheetHasActiveFilters(effectiveFilters) ? (
                  <button
                    type="button"
                    onClick={() => reset()}
                    className="rounded-lg px-2 py-1 text-xs font-medium text-accent transition hover:bg-fill-tertiary"
                  >
                    Zurücksetzen
                  </button>
                ) : null
              }
            >
              <FilterBar
                filters={effectiveFilters}
                timesheets={allTimesheets}
                availableYears={yearChips}
                user={user}
                onChange={update}
                onReset={reset}
              />
            </CollapsibleSection>

            {error ? (
              <Alert variant="error" title="Liste konnte nicht geladen werden">
                {error}
              </Alert>
            ) : null}

            <section className="space-y-3">
              {!loading && filteredRows.length > 0 ? (
                <CollapsibleSection sectionKey="stundennachweise.kennzahlen" title="Kennzahlen">
                  <TimesheetSummaryPanels
                    rows={filteredRows}
                    user={user}
                    searchQuery={filters.q}
                    onContactAgingFilter={handleContactAgingFilter}
                  />
                </CollapsibleSection>
              ) : null}

              {loading && allTimesheets.length === 0 ? (
                <div className="flex items-center justify-center py-16 text-label-secondary">
                  <Spinner />
                  <span className="ml-2 text-sm">Stundennachweise werden geladen …</span>
                </div>
              ) : (
                <>
                  <TimesheetTable
                    rows={filteredRows}
                    user={user}
                    loading={loading}
                    columns={visibleColumns}
                    heroFields={heroFields}
                    hoursUsageById={hoursUsageById}
                    widths={visibleWidths}
                    onRowOpen={handleRowOpen}
                    onContactToggled={patchTimesheetAtContact}
                    onColumnWidthChange={handleColumnWidthChange}
                  />
                  {!loading &&
                  filteredRows.length === 0 &&
                  !error &&
                  timesheetHasActiveFilters(effectiveFilters) ? (
                    <div className="mt-4 flex flex-col items-center gap-3 rounded-ios bg-fill-quaternary/25 px-4 py-4 text-center shadow-ios ring-1 ring-separator/30 dark:ring-separator/45">
                      <p className="max-w-md text-sm text-label-secondary">
                        Für die aktuelle Auswahl wurden keine Stundennachweise gefunden. Alle Filter
                        zurücksetzen, um die Chance auf Treffer zu erhöhen.
                      </p>
                      <Button type="button" variant="secondary" onClick={() => reset()}>
                        Filter zurücksetzen
                      </Button>
                    </div>
                  ) : null}
                </>
              )}
            </section>
          </>
        )}
    </div>
  );

  // Büro unter Dokumente: linke Navigation kommt aus DokumenteLayout.
  // Standalone (z. B. alte Links ohne Redirect): weiter lokale Tabs.
  const showInternalSidebar = isBuero && !inDokumenteSection && !section;

  return (
    <AppShell
      title={
        isCompletionsTab ? "Fertigstellungsmeldungen" : "Stundennachweise"
      }
    >
      {showInternalSidebar ? (
        <div className="eh-bestellwesen-page-shell">
          <SectionSidebarNav items={sectionNavItems} ariaLabel="Stundennachweise" />
          <div className="eh-project-page-main">{pageContent}</div>
        </div>
      ) : (
        pageContent
      )}

      <TimesheetColumnsDialog
        open={columnsDialogOpen}
        onClose={() => setColumnsDialogOpen(false)}
        heroFields={heroFields}
        columnOptions={columnOptions}
        settings={columnSettings}
        onSave={handleSaveColumnSettings}
      />
    </AppShell>
  );
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);
    onChange();
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}
